import React, { ChangeEvent, FC } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";

import ButtonPrimary from "../../../../components/ButtonPrimary";
import Button from "../../../../components/Button";
import Modal from "../../../../components/Modal";
import { ModalHeader } from "../../../../components/Modal/ModalHeader";
import { ModalContent } from "../../../../components/Modal/ModalContent";
import { ModalFooter } from "../../../../components/Modal/ModalFooter";
import FormGroup from "../../../../components/FormGroup";
import Label from "../../../../components/Label";
import Input from "../../../../components/Input";
import { ArticleLimitModalProps, IArticleLimitFormData } from "./types";
import { validationRules } from "../../../../utils/common/contants";

import "./styles.scss";

export const ArticleLimitModal: FC<ArticleLimitModalProps> = ({
  isOpen,
  limitValue,
  onDismiss,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const { handleSubmit, formState, errors, control } = useForm<
    IArticleLimitFormData
  >({
    mode: "onChange",
    defaultValues: {
      limit: limitValue ? `${limitValue}` : "0",
    },
  });

  const onInputChange = (event: ChangeEvent<HTMLInputElement>, props: any) => {
    const value = event.target.value;
    const isValueValid = validationRules.number.pattern.onlyNumber.test(value);
    if (!isValueValid && value !== "") {
      return;
    }
    props.onChange(value);
  };

  const errorMessage = (type?: string): string => {
    return (
      ((type === "pattern" || type === "min" || type === "required") &&
        t("youMustEnterWholeNumber")) ||
      (type === "max" && t("exceededNumberOfArticles")) ||
      ""
    );
  };

  return (
    <Modal
      id="article-limit-modal"
      className="ams-article-limit-modal"
      isOpen={isOpen}
      onDismiss={onDismiss}
    >
      <ModalHeader>{t("setArticleLimit")}</ModalHeader>
      <form onSubmit={handleSubmit(onUpdate)}>
        <ModalContent className="ams-article-limit-modal__content">
          <FormGroup className="ams-article-limit-modal__form-group mb-xl">
            <Label>{t("articleLimit")}</Label>
            <Controller
              name="limit"
              control={control}
              rules={{
                required: true,
                min: 0,
                max: validationRules.number.size.uint,
                pattern: validationRules.number.pattern.noLeadingZeroInt,
              }}
              render={(props) => {
                return (
                  <Input
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      onInputChange(e, props);
                    }}
                    value={props.value}
                    type="number"
                    error={!!errors.limit}
                    errorText={errorMessage(errors?.limit?.type)}
                    description={`0 = ${t("unlimited")}`}
                    id="article-limit-input"
                  />
                );
              }}
            />
          </FormGroup>
        </ModalContent>
        <ModalFooter className="mt-0">
          <Button
            onClick={onDismiss}
            className="ams-modal__action"
            id="cancel-button"
          >
            {t("cancel")}
          </Button>
          <ButtonPrimary
            id="update-button"
            type="submit"
            className="ams-modal__action"
            disabled={!formState.isValid}
          >
            {t("update")}
          </ButtonPrimary>
        </ModalFooter>
      </form>
    </Modal>
  );
};

ArticleLimitModal.defaultProps = {
  limitValue: 0,
};
